import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import BrandSelect from "./BrandSelect";
import { AdyenMerchantBrand, Brand } from "../../types/paymentProcessor";
import { BRAND_LABEL } from "../../constants/brand";
import { useApi } from "../../hooks/useApi";

interface AdyenMerchantBrandsDialogProps {
  open: boolean;
  accountId: number | null;
  accountName?: string;
  apiBase: string;
  brands: Brand[];
  refreshBrands: () => void;
  createBrandUrl?: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function AdyenMerchantBrandsDialog({
  open,
  accountId,
  accountName,
  apiBase,
  brands,
  refreshBrands,
  createBrandUrl,
  onClose,
  onSaved,
}: AdyenMerchantBrandsDialogProps) {
  const { get, loading } = useApi();
  const { put } = useApi();
  const [merchants, setMerchants] = useState<AdyenMerchantBrand[] | null>(null);
  const [brandByCode, setBrandByCode] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const soleBrandId = brands.length === 1 ? brands[0].brandId : null;

  const loadMerchants = useCallback(async () => {
    if (accountId == null) return;
    setError(null);
    try {
      const res = await get<{ merchants: AdyenMerchantBrand[] }>(`${apiBase}/${accountId}/merchant-brands`);
      setMerchants(res.merchants);
    } catch (err: any) {
      setMerchants(null);
      setError(err.message ?? 'Failed to load Adyen merchant accounts');
    }
  }, [accountId, apiBase, get]);

  useEffect(() => {
    if (!open) return;
    setMerchants(null);
    setBrandByCode({});
    loadMerchants();
  }, [open, loadMerchants]);

  const getBrandId = (merchant: AdyenMerchantBrand): number | null =>
    brandByCode[merchant.merchantAccountCode] ?? merchant.brandId ?? soleBrandId;

  const unmappedCount = (merchants ?? []).filter((m) => getBrandId(m) == null).length;
  const canSave = merchants != null && merchants.length > 0 && unmappedCount === 0 && !saving;

  const handleSave = async () => {
    if (accountId == null || !merchants) return;
    setSaving(true);
    setError(null);
    try {
      await put(`${apiBase}/${accountId}/merchant-brands`, {
        mappings: merchants.map((m) => ({
          merchantAccountCode: m.merchantAccountCode,
          brandId: getBrandId(m),
        })),
      });
      onClose();
      await onSaved();
    } catch (err: any) {
      setError(err.message ?? 'Failed to save merchant mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Map Adyen merchants to {BRAND_LABEL.plural.toLowerCase()}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          One Adyen account covers every merchant account under the Adyen company
          {accountName ? ` ${accountName}` : ''}. Disputes take their {BRAND_LABEL.singular.toLowerCase()} from
          the merchant account they arrived on, so each one needs a {BRAND_LABEL.singular.toLowerCase()}.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && merchants == null && !error && (
          <Typography variant="body2">Loading merchant accounts...</Typography>
        )}

        {merchants?.length === 0 && (
          <Alert severity="warning">Adyen returned no merchant accounts for this account.</Alert>
        )}

        {merchants && merchants.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Merchant account</TableCell>
                  <TableCell sx={{ width: 220 }}>{BRAND_LABEL.singular}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {merchants.map((merchant) => (
                  <TableRow key={merchant.merchantAccountCode}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                          {merchant.merchantAccountCode}
                        </Box>
                        {merchant.missingInAdyen && (
                          <Chip
                            label="Missing in Adyen"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                      {merchant.name && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {merchant.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <BrandSelect
                        brands={brands}
                        value={getBrandId(merchant) ?? ''}
                        onChange={(brandId) => setBrandByCode((prev) => ({
                          ...prev,
                          [merchant.merchantAccountCode]: brandId,
                        }))}
                        refreshBrands={refreshBrands}
                        disabled={saving}
                        createBrandUrl={createBrandUrl}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: unmappedCount > 0 ? 'warning.main' : 'text.secondary' }}>
          {unmappedCount > 0
            ? `${unmappedCount} merchant account${unmappedCount === 1 ? ' still needs' : 's still need'} a ${BRAND_LABEL.singular.toLowerCase()}`
            : ''}
        </Typography>
        <Box>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
